import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Logo from '../assets/images/arka-saydam-logo.png';
import { API_BASE_URL } from '../config';
import { getClientInstallId } from '../utils/clientInstallId';
import { resolvePostLoginRoute } from '../utils/resolvePostLoginRoute';

const API_URL = API_BASE_URL;

// isDarkMode ve toggleDarkMode prop'larını kabul ediyoruz.
const LoginPage = ({ isDarkMode, toggleDarkMode }) => {
    // State: Şifre görünürlüğünü yönetir
    const [passwordVisible, setPasswordVisible] = useState(false);
    const [email, setEmail] = useState('');
    const [passwordVal, setPasswordVal] = useState('');
    const navigate = useNavigate(); // Yönlendirme için

    // Token varsa kullanıcıyı doğrudan içeri al
    useEffect(() => {
        const authToken = localStorage.getItem('authToken');
        if (!authToken) return;

        fetch(`${API_URL}/auth/me`, {
            credentials: 'include',
            headers: { Authorization: `Bearer ${authToken}` }
        })
            .then(async (res) => {
                if (!res.ok) {
                    localStorage.removeItem('authToken');
                    return null;
                }
                return res.json();
            })
            .then((user) => {
                if (!user) return;
                resolvePostLoginRoute()
                    .then((route) => navigate(route, { replace: true }))
                    .catch(() => navigate('/registration-survey', { replace: true }));
            })
            .catch(() => {
                localStorage.removeItem('authToken');
            });
    }, [navigate]);

    // Eğer Google OAuth ile geri dönersek, backend'den /auth/me çağırıp oturum açıldıysa yönlendir.
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get('auth') === 'success') {
            const authToken = localStorage.getItem('authToken');
            fetch(`${API_URL}/auth/me`, {
                credentials: 'include',
                headers: authToken ? { Authorization: `Bearer ${authToken}` } : {}
            })
                .then(r => r.json())
                .then(user => {
                    if (user) {
                        resolvePostLoginRoute()
                            .then((route) => navigate(route))
                            .catch(() => navigate('/registration-survey'));
                    }
                })
                .catch(() => { });
        }
    }, [navigate]);

    // Giriş Formu Gönderme Fonksiyonu
    const handleSubmit = async (e) => {
        e.preventDefault();
        console.log('🚀 Login attempt for:', email);
        try {
            const res = await fetch(`${API_URL}/auth/login`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json', 'x-client-install-id': getClientInstallId() },
                body: JSON.stringify({ email, password: passwordVal })
            });
            console.log('📡 Login response status:', res.status);

            if (res.ok) {
                const data = await res.json().catch(() => ({}));
                console.log('✅ Login successful, token received:', !!data.token);
                if (data && data.token) {
                    localStorage.setItem('authToken', data.token);
                    sessionStorage.setItem('justLoggedIn', 'true');
                }
                try {
                    const route = await resolvePostLoginRoute();
                    navigate(route);
                } catch (e) {
                    console.error('Status check failed:', e);
                    navigate('/registration-survey');
                }
            } else {
                const data = await res.json().catch(() => ({}));
                console.error('❌ Login failed:', data.error || 'Unknown error');
                alert(data.error || 'Giriş başarısız');
            }
        } catch (err) {
            console.error('🔥 Connection error:', err);
            alert('Sunucuya bağlanılamadı');
        }
    };

    return (
        // Sayfa Arka Planı: Light Mode: bg-white, Dark Mode: bg-[#1a0a0a] (SignupPage ile uyumlu)
        <div className="min-h-screen flex flex-col justify-center items-center p-4 relative bg-white dark:bg-[#1a1a1a]">

            {/* 1. Dark Mode Toggle Butonu (Sağ Üstte Sabit) */}
            <button
                className="fixed top-5 right-5 flex items-center justify-center size-10 rounded-full hover:bg-gray-100 dark:hover:bg-white/10 transition-colors text-gray-500 dark:text-gray-300 z-50"
                type="button"
                onClick={toggleDarkMode}
                aria-label="Temayı değiştir"
            >
                <span className="material-symbols-outlined">
                    {/* isDarkMode prop'una göre ikonu değiştirir */}
                    {isDarkMode ? 'dark_mode' : 'light_mode'}
                </span>
            </button>


            {/* Background Decoration (Dark Mode'da görünür) */}
            <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden hidden dark:block">
                <div className="absolute -top-[20%] -left-[10%] w-[50%] h-[50%] bg-primary/5 blur-[120px] rounded-full"></div>
                <div className="absolute top-[40%] -right-[10%] w-[40%] h-[40%] bg-primary/5 blur-[120px] rounded-full"></div>
            </div>

            {/* Main Card Container */}
            <div className="relative z-10 w-full max-w-[480px] flex flex-col gap-6 pt-10 pb-16">
                {/* 1. Header Section */}
                <div className="flex flex-col items-center gap-4">
                    {/* Logo/Profil Resmi */}
                    <div
                        // ring-4 ring-gray-100 dark:ring-[#2a1515] (SignupPage ile uyumlu hale getirildi)
                        className="bg-center bg-no-repeat bg-cover rounded-full h-24 w-24 shadow-lg ring-4 ring-gray-100 dark:ring-[#2a1515]"
                        data-alt="Abstract minimalist chef hat logo with steam on dark background"
                        style={{ backgroundImage: `url(${Logo})` }}
                    ></div>
                    <div className="flex flex-col items-center text-center">
                        {/* text-gray-900 dark:text-white (Dark/Light uyumlu metin) */}
                        <h1 className="text-gray-900 dark:text-white text-2xl font-bold tracking-tight">Bana Bir Tarif Ver</h1>
                        {/* text-gray-500 dark:text-gray-400 (Muted metin) */}
                        <p className="text-gray-500 dark:text-gray-400 text-sm font-normal mt-1">Bugün ne yemek yapsam</p>
                    </div>
                </div>

                {/* 2. Segmented Buttons (Tabs) */}
                {/* bg-gray-100 dark:bg-[#2a1515] border border-gray-200 dark:border-gray-800 (SignupPage ile uyumlu hale getirildi) */}
                <div className="bg-gray-100 dark:bg-[#2a1515] p-1 rounded-xl flex shadow-sm border border-gray-200 dark:border-gray-800">

                    {/* 2.1. Giriş Yap (Active Tab) */}
                    <Link to="/giris-yap" aria-current="page" className="flex-1 text-center">
                        <div className="flex items-center justify-center py-2.5 px-4 rounded-lg text-sm font-medium bg-primary text-white shadow-md">
                            Giriş Yap
                        </div>
                    </Link>

                    {/* 2.2. Kayıt Ol (Link to Signup Page) */}
                    <Link to="/kayit-ol" className="flex-1 text-center">
                        {/* text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-white dark:hover:bg-white/5 (Dark/Light uyumlu hale getirildi) */}
                        <div className="flex items-center justify-center py-2.5 px-4 rounded-lg text-sm font-medium text-gray-500 dark:text-gray-400 transition-all hover:text-gray-900 dark:hover:text-white hover:bg-white dark:hover:bg-white/5">
                            Kayıt Ol
                        </div>
                    </Link>
                </div>

                {/* 3. Form Section */}
                <form className="flex flex-col gap-5" onSubmit={handleSubmit}> {/* onSubmit güncellendi */}
                    {/* Email */}
                    <label className="flex flex-col gap-1.5">
                        <span className="text-gray-900 dark:text-white text-sm font-medium">E-posta Adresi</span>
                        <div className="relative flex items-center">
                            <input
                                // bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white (Dark/Light uyumlu input)
                                className="w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-4 py-3.5 pl-4 pr-12 placeholder-gray-400 dark:placeholder-gray-500 text-gray-900 dark:text-white focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors"
                                placeholder="ornek@email.com"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                            />
                            {/* text-gray-500 dark:text-gray-400 (Muted ikon) */}
                            <span className="material-symbols-outlined absolute right-4 text-gray-500 dark:text-gray-400 pointer-events-none">mail</span>
                        </div>
                    </label>

                    {/* Password */}
                    <label className="flex flex-col gap-1.5">
                        <span className="text-gray-900 dark:text-white text-sm font-medium">Şifre</span>
                        <div className="relative flex items-center">
                            <input
                                // bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white (Dark/Light uyumlu input)
                                className="w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-4 py-3.5 pl-4 pr-12 placeholder-gray-400 dark:placeholder-gray-500 text-gray-900 dark:text-white focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors"
                                type={passwordVisible ? 'text' : 'password'}
                                value={passwordVal}
                                onChange={(e) => setPasswordVal(e.target.value)}
                                required
                            />
                            <button
                                // text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white (Dark/Light uyumlu buton)
                                className="absolute right-4 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white flex items-center justify-center"
                                type="button"
                                onClick={() => setPasswordVisible(p => !p)}
                            >
                                <span className="material-symbols-outlined">{passwordVisible ? 'visibility' : 'visibility_off'}</span>
                            </button>
                        </div>
                        {/* Şifremi Unuttum Linki */}
                        <div className="flex justify-end pt-0.5">
                            <Link to="/sifremi-unuttum" className="text-primary text-xs font-semibold hover:text-primary-dark transition-colors">
                                Şifremi Unuttum
                            </Link>
                        </div>
                    </label>

                    {/* Submit Button (Aynı kaldı) */}
                    <button className="mt-2 w-full bg-primary hover:bg-primary-dark text-white font-bold py-3.5 px-4 rounded-lg shadow-lg shadow-red-900/20 transition-all active:scale-[0.98]" type="submit">
                        Giriş Yap
                    </button>

                    {/* VEYA Yazısı */}
                    <div className="relative flex py-2 items-center">
                        {/* border-gray-200 dark:border-gray-700 (Dark/Light uyumlu çizgi) */}
                        <div className="flex-grow border-t border-gray-200 dark:border-gray-700"></div>
                        {/* text-gray-500 dark:text-gray-400 (Muted metin) */}
                        <span className="flex-shrink-0 mx-4 text-gray-500 dark:text-gray-400 text-xs uppercase">veya</span>
                        <div className="flex-grow border-t border-gray-200 dark:border-gray-700"></div>
                    </div>

                    {/* Google Button */}
                    <button
                        // bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white (Dark/Light uyumlu buton)
                        className="w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-900 dark:text-white font-medium py-3.5 px-4 rounded-lg flex items-center justify-center gap-3 transition-colors"
                        type="button"
                        onClick={() => { window.location.href = `${API_URL}/auth/google`; }}
                    >
                        {/* Google SVG (Aynı kaldı) */}
                        <svg className="w-5 h-5" viewBox="0 0 24 24">
                            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"></path>
                            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"></path>
                            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"></path>
                            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"></path>
                        </svg>
                        Google ile Devam Et
                    </button>
                </form>

                {/* En Alttaki Kayıt Ol Yazısı */}
                {/* text-gray-500 dark:text-gray-400 (Muted metin) */}
                <p className="text-center text-xs text-gray-500 dark:text-gray-400 mt-2">
                    Henüz bir hesabın yok mu <Link className="text-primary hover:underline" to="/kayit-ol">Kayıt Ol</Link>
                </p>
            </div>
        </div>
    );
};

export default LoginPage;
